import { useCallback } from 'react'
import { saveAs } from 'file-saver'
import * as XLSX from 'xlsx'
import {
  FnsServiceGetSurveysForAdminQuery,
  FnsServiceGetFormsForAdminQuery,
  useFnsServiceGetSurveysForAdminLazyQuery,
  useFnsServiceGetFormsForAdminLazyQuery,
} from 'zma-react-sdk/fns-service'
import { useTenantStore } from '@/stores/tenantStore'

type SurveyType = NonNullable<
  FnsServiceGetSurveysForAdminQuery['fnsServiceGetSurveysForAdmin']
>['data'][0]

type FormType = NonNullable<
  FnsServiceGetFormsForAdminQuery['fnsServiceGetFormsForAdmin']
>['data'][0]

interface ExportOptions {
  filename?: string
  sheetName?: string
}

interface ExportAllOptions extends ExportOptions {
  searchTerm?: string
  pageSize?: number
  onProgress?: (current: number, total: number) => void
}

export function useExcelExport() {
  const [getSurveysLazy] = useFnsServiceGetSurveysForAdminLazyQuery()
  const [getFormsLazy] = useFnsServiceGetFormsForAdminLazyQuery()
  const selectedTenantId = useTenantStore((state) => state.selectedTenantId)

  const exportSurveysToExcel = useCallback(
    (surveys: SurveyType[], options?: ExportOptions) => {
      const { filename = 'surveys-export', sheetName = 'Surveys' } =
        options || {}

      // Prepare data for Excel export
      const exportData = surveys.map((survey) => ({
        'Survey ID': survey.id || '-',
        'Survey Name': survey.title || '-',
        Type: survey.type || '-',
        Status: survey.isActive ? 'Active' : 'Inactive',
        'Is Anonymous': survey.isAnonymous ? 'Yes' : 'No',
        'Question Count': survey.questions?.length || 0,
        'Created At': survey.createdAt
          ? new Date(survey.createdAt).toLocaleString()
          : '-',
        'Updated At': survey.updatedAt
          ? new Date(survey.updatedAt).toLocaleString()
          : '-',
        'Published At': survey.publishedAt
          ? new Date(survey.publishedAt).toLocaleString()
          : '-',
      }))

      // Create workbook and worksheet
      const workbook = XLSX.utils.book_new()
      const worksheet = XLSX.utils.json_to_sheet(exportData)

      // Auto-adjust column widths
      const columnWidths = [
        { wch: 30 }, // Survey ID
        { wch: 40 }, // Survey Name
        { wch: 15 }, // Type
        { wch: 12 }, // Status
        { wch: 12 }, // Is Anonymous
        { wch: 15 }, // Question Count
        { wch: 20 }, // Created At
        { wch: 20 }, // Updated At
        { wch: 20 }, // Published At
      ]
      worksheet['!cols'] = columnWidths

      // Add the worksheet to the workbook
      XLSX.utils.book_append_sheet(workbook, worksheet, sheetName)

      // Generate Excel file and save
      const excelBuffer = XLSX.write(workbook, {
        bookType: 'xlsx',
        type: 'array',
      })

      const blob = new Blob([excelBuffer], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      })

      const timestamp = new Date()
        .toISOString()
        .slice(0, 16)
        .replace(/[:.]/g, '-')
      saveAs(blob, `${filename}-${timestamp}.xlsx`)
    },
    []
  )

  const exportFormsToExcel = useCallback(
    (forms: FormType[], options?: ExportOptions) => {
      const { filename = 'forms-export', sheetName = 'Forms' } = options || {}

      // Prepare data for Excel export
      const exportData = forms.map((form) => ({
        'Form ID': form.id || '-',
        'Form Name': form.title || '-',
        Status: form.isActive ? 'Active' : 'Inactive',
        'Is Anonymous': form.isAnonymous ? 'Yes' : 'No',
        'Question Count': form.questions?.length || 0,
        'Created At': form.createdAt
          ? new Date(form.createdAt).toLocaleString()
          : '-',
        'Updated At': form.updatedAt
          ? new Date(form.updatedAt).toLocaleString()
          : '-',
      }))

      // Create workbook and worksheet
      const workbook = XLSX.utils.book_new()
      const worksheet = XLSX.utils.json_to_sheet(exportData)

      // Auto-adjust column widths
      const columnWidths = [
        { wch: 30 }, // Form ID
        { wch: 40 }, // Form Name
        { wch: 12 }, // Status
        { wch: 12 }, // Is Anonymous
        { wch: 15 }, // Question Count
        { wch: 20 }, // Created At
        { wch: 20 }, // Updated At
      ]
      worksheet['!cols'] = columnWidths

      // Add the worksheet to the workbook
      XLSX.utils.book_append_sheet(workbook, worksheet, sheetName)

      // Generate Excel file and save
      const excelBuffer = XLSX.write(workbook, {
        bookType: 'xlsx',
        type: 'array',
      })

      const blob = new Blob([excelBuffer], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      })

      const timestamp = new Date()
        .toISOString()
        .slice(0, 16)
        .replace(/[:.]/g, '-')
      saveAs(blob, `${filename}-${timestamp}.xlsx`)
    },
    []
  )

  const exportAllSurveysToExcel = useCallback(
    async (options: ExportAllOptions) => {
      const {
        searchTerm,
        pageSize = 50,
        filename = 'surveys-all-export',
        sheetName = 'Surveys',
        onProgress,
      } = options

      const allSurveys: SurveyType[] = []
      let currentPage = 0
      let hasMoreData = true

      try {
        while (hasMoreData) {
          const skip = currentPage * pageSize

          const { data } = await getSurveysLazy({
            variables: {
              pagination: {
                limit: pageSize,
                skip,
                ...(searchTerm && { search: searchTerm }),
              },
            },
            context: {
              headers: {
                'x-tenant-id': selectedTenantId,
              },
            },
            fetchPolicy: 'network-only',
          })

          const surveys = data?.fnsServiceGetSurveysForAdmin?.data || []

          if (surveys.length > 0) {
            allSurveys.push(...surveys)
            onProgress?.(allSurveys.length, allSurveys.length + pageSize)

            // If we got less than the limit, we've reached the end
            if (surveys.length < pageSize) {
              hasMoreData = false
            } else {
              currentPage++
            }
          } else {
            hasMoreData = false
          }
        }

        // Export all collected surveys
        exportSurveysToExcel(allSurveys, { filename, sheetName })

        return allSurveys.length
      } catch (error) {
        console.error('Error exporting all surveys:', error)
        throw error
      }
    },
    [getSurveysLazy, selectedTenantId, exportSurveysToExcel]
  )

  const exportAllFormsToExcel = useCallback(
    async (options: ExportAllOptions) => {
      const {
        searchTerm,
        pageSize = 50,
        filename = 'forms-all-export',
        sheetName = 'Forms',
        onProgress,
      } = options

      const allForms: FormType[] = []
      let currentPage = 0
      let hasMoreData = true

      try {
        while (hasMoreData) {
          const skip = currentPage * pageSize

          const { data } = await getFormsLazy({
            variables: {
              pagination: {
                limit: pageSize,
                skip,
                ...(searchTerm && { search: searchTerm }),
              },
            },
            context: {
              headers: {
                'x-tenant-id': selectedTenantId,
              },
            },
            fetchPolicy: 'network-only',
          })

          const forms = data?.fnsServiceGetFormsForAdmin?.data || []

          if (forms.length > 0) {
            allForms.push(...forms)
            onProgress?.(allForms.length, allForms.length + pageSize)

            // If we got less than the limit, we've reached the end
            if (forms.length < pageSize) {
              hasMoreData = false
            } else {
              currentPage++
            }
          } else {
            hasMoreData = false
          }
        }

        // Export all collected forms
        exportFormsToExcel(allForms, { filename, sheetName })

        return allForms.length
      } catch (error) {
        console.error('Error exporting all forms:', error)
        throw error
      }
    },
    [getFormsLazy, selectedTenantId, exportFormsToExcel]
  )

  return {
    exportSurveysToExcel,
    exportFormsToExcel,
    exportAllSurveysToExcel,
    exportAllFormsToExcel,
  }
}
